import React, { useState } from "react";
import { Link } from "react-router-dom";
import { IFeedAchievementItem, postShareDescription, postShareTitle } from "@eagerdog/interfaces";
import moment from "moment";

import { toast } from "src/components/Toast/ToastManager";
import Button from "src/components/Button/Button";
import { Modal, useModal } from "src/components/Modal/Modal";
import ShareModal from "src/components/ShareModal/ShareModal";
import { apiService } from "src/services/api.service";
import { achievementPath, webOrigin } from "./postLinks";

import styles from "./AchievementCard.module.scss";

interface IProps {
    item: IFeedAchievementItem;
    /** True on /p/:id — the timestamp is plain text instead of a link to itself. */
    isPermalink?: boolean;
}

const AchievementCard: React.FC<IProps> = ({ item, isPermalink = false }) => {
    const [congratulated, setCongratulated] = useState(false);
    const shareModal = useModal();
    // The achievement item is the title_earned post; its id is the post id.
    const shareUrl = `${webOrigin()}${achievementPath(item)}`;
    const timestamp = moment(item.created_at).fromNow();

    const detailParts = [
        [item.sanctioning_club, item.show_type].filter(Boolean).join(" "),
        item.points ? `${Math.round(item.points)} points` : null,
        item.dog_breed,
    ].filter(Boolean);

    const stats: [string, string][] = [
        ["fastest mph", item.fastest_mph != null ? item.fastest_mph.toFixed(2) : "—"],
        ["average mph", item.average_mph != null ? item.average_mph.toFixed(2) : "—"],
        ["qualifying runs", item.total_runs != null ? String(item.total_runs) : "—"],
    ];

    const dogLink = item.dog_id ? `/dogs/${item.dog_id}` : undefined;
    const dogName = dogLink
        ? <Link to={dogLink} className={styles.nameLink}>{item.dog_call_name}</Link>
        : item.dog_call_name;
    const avatar = item.dog_image_url
        ? <img src={item.dog_image_url} alt={item.dog_call_name} />
        : <span>{item.dog_call_name.charAt(0).toUpperCase()}</span>;

    return (
        <div className={styles.AchievementCard}>
            <div className={styles.highlight}>
                {dogLink
                    ? <Link to={dogLink} className={styles.avatar}>{avatar}</Link>
                    : <div className={styles.avatar}>{avatar}</div>}
                <div className={styles.text}>
                    <div className={styles.label}>New title</div>
                    <div className={styles.title}>{dogName} earned {item.title_name}</div>
                    {item.owner_display_name && <div className={styles.owner}>{item.owner_display_name}</div>}
                    {detailParts.length > 0 && <div className={styles.detail}>{detailParts.join(" · ")}</div>}
                </div>
                {item.is_own_dog ? (
                    <Button variant="secondary" className={styles.congratulateBtn} onClick={shareModal.toggle}>
                        Share
                    </Button>
                ) : (
                    <Button
                        className={styles.congratulateBtn}
                        disabled={congratulated}
                        onClick={() => {
                            setCongratulated(true);
                            toast.show({ title: "Sent!", content: `${item.dog_call_name} has been congratulated.`, duration: 5000, type: "success" });
                        }}
                    >
                        {congratulated ? "Congratulated" : "Congratulate"}
                    </Button>
                )}
            </div>
            <div className={styles.statsRow}>
                {stats.map(([label, value]) => (
                    <div className={styles.stat} key={label}>
                        <div className={styles.statValue}>{value}</div>
                        <div className={styles.statLabel}>{label}</div>
                    </div>
                ))}
            </div>
            <div className={styles.timestamp}>
                {isPermalink
                    ? timestamp
                    : <Link to={achievementPath(item)} className={styles.timeLink}>{timestamp}</Link>}
            </div>
            <Modal
                className="ShareModalContainer"
                isShown={shareModal.isShown}
                hide={shareModal.toggle}
                modalContent={
                    <ShareModal
                        eventTitle={postShareTitle(item)}
                        shareText={postShareDescription(item) || undefined}
                        eventUrl={shareUrl}
                        shareType="post"
                        onClose={shareModal.toggle}
                        onShare={(channel) => {
                            apiService.recordPostShare(item.id, channel).catch(() => {});
                        }}
                    />
                }
            />
        </div>
    );
};

export default AchievementCard;
